import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { Edit, Trash2, Key, UserPlus, Users, Search } from "lucide-react";
import { showConfirm, showError, showSuccess } from "@/utils/dialog";

const PAGE_SIZE = 10;
const REPORT_PERMISSION_OPTIONS = [
  { key: "profit_loss", label: "Profit & Loss" },
  { key: "collections", label: "Collections" },
  { key: "outstanding_aging", label: "Outstanding Aging" },
  { key: "paid_invoices", label: "Paid Invoices" },
  { key: "expense_by_category", label: "Expense by Category" },
  { key: "pl_statement", label: "P&L Statement" },
  { key: "ledger_summary", label: "Ledger Summary" },
  { key: "daily_operations", label: "Daily Operations" },
  { key: "sales_funnel", label: "Sales Funnel" },
  { key: "revenue_by_period", label: "Revenue by Period" },
  { key: "revenue_by_salesperson", label: "Revenue by Salesperson" },
  { key: "revenue_by_service_type", label: "Revenue by Service Type" },
  { key: "revenue_by_client", label: "Revenue by Client" },
  { key: "export_pdf", label: "Export PDF" },
  { key: "export_excel", label: "Export Excel" },
];

export default function TeamManagement() {
  const { user: currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "sales",
    password: "",
    report_permissions: { enabled: false, allowed_reports: [] },
  });
  const [emailError, setEmailError] = useState("");
  const [checkingEmail, setCheckingEmail] = useState(false);
  const debounceTimer = useRef(null);

  const [passwordData, setPasswordData] = useState({
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const data = await api.users.getAll();
      setTeamMembers(data);
    } catch (error) {
      console.error("Failed to fetch team members:", error);
      setError("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = useCallback(async (email) => {
    if (!email || !email.includes("@")) {
      setEmailError("");
      return;
    }

    setCheckingEmail(true);
    setEmailError("");

    try {
      const response = await api.users.checkEmail(email);
      if (response.exists) {
        setEmailError("This email is already registered");
      } else {
        setEmailError("");
      }
    } catch (error) {
      console.error("Email validation error:", error);
      // Don't show error on validation failure, just log it
    } finally {
      setCheckingEmail(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Real-time email validation with debouncing
    if (name === "email") {
      setEmailError(""); // Clear previous error on change

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer for debounced validation
      if (value && value.includes("@")) {
        debounceTimer.current = setTimeout(() => {
          validateEmail(value);
        }, 500);
      } else {
        setEmailError("");
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReportPermissionToggle = (enabled) => {
    setFormData((prev) => ({
      ...prev,
      report_permissions: {
        ...(prev.report_permissions || { enabled: false, allowed_reports: [] }),
        enabled,
      },
    }));
  };

  const handleReportSelection = (reportKey, checked) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.report_permissions?.allowed_reports)
        ? prev.report_permissions.allowed_reports
        : [];
      const next = checked
        ? [...new Set([...current, reportKey])]
        : current.filter((k) => k !== reportKey);
      return {
        ...prev,
        report_permissions: {
          ...(prev.report_permissions || { enabled: false, allowed_reports: [] }),
          allowed_reports: next,
        },
      };
    });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (
      !formData.name ||
      !formData.email ||
      !formData.role ||
      !formData.password
    ) {
      setError("All fields are required");
      setSubmitting(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      setSubmitting(false);
      return;
    }

    // Check email one more time before submitting
    if (formData.email) {
      const emailCheck = await api.users.checkEmail(formData.email);
      if (emailCheck.exists) {
        setEmailError("This email is already registered");
        setSubmitting(false);
        return;
      }
    }

    try {
      await api.users.create(formData);
      setShowAddModal(false);
      setFormData({
        name: "",
        email: "",
        role: "sales",
        password: "",
        report_permissions: { enabled: false, allowed_reports: [] },
      });
      setEmailError("");
      fetchTeamMembers();
    } catch (error) {
      console.error("Failed to create team member:", error);
      if (
        error.message?.includes("Email already registered") ||
        error.message?.includes("email")
      ) {
        setEmailError("This email is already registered");
      } else {
        setError(
          error.message || "Failed to create team member. Please try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!formData.name || !formData.role) {
      setError("Name and role are required");
      setSubmitting(false);
      return;
    }

    try {
      await api.users.update(selectedUser.id, {
        name: formData.name,
        role: formData.role,
        is_active: formData.is_active !== undefined ? formData.is_active : true,
        report_permissions: formData.report_permissions || { enabled: false, allowed_reports: [] },
      });
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({
        name: "",
        email: "",
        role: "sales",
        password: "",
        report_permissions: { enabled: false, allowed_reports: [] },
      });
      fetchTeamMembers();
    } catch (error) {
      console.error("Failed to update team member:", error);
      setError(
        error.message || "Failed to update team member. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMember = async (userId) => {
    const confirmed = await showConfirm(
      "Are you sure you want to delete this team member?",
      "Delete Team Member",
      "Delete",
    );
    if (!confirmed) {
      return;
    }

    try {
      await api.users.delete(userId);
      fetchTeamMembers();
    } catch (error) {
      console.error("Failed to delete team member:", error);
      await showError(error.message || "Failed to delete team member. Please try again.");
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    if (!passwordData.new_password || !passwordData.confirm_password) {
      setError("Both password fields are required");
      setSubmitting(false);
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError("Passwords do not match");
      setSubmitting(false);
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError("Password must be at least 6 characters");
      setSubmitting(false);
      return;
    }

    try {
      await api.users.resetPassword(selectedUser.id, passwordData.new_password);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setPasswordData({ new_password: "", confirm_password: "" });
      await showSuccess("Password reset successfully");
    } catch (error) {
      console.error("Failed to reset password:", error);
      setError(error.message || "Failed to reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      password: "",
      report_permissions: user.report_permissions || { enabled: false, allowed_reports: [] },
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({ new_password: "", confirm_password: "" });
    setShowPasswordModal(true);
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700",
      sales: "bg-blue-100 text-blue-700",
      finance: "bg-emerald-100 text-emerald-700",
      management: "bg-amber-100 text-amber-700",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${
          styles[role] || styles.sales
        }`}
      >
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  const filteredMembers = useMemo(() => {
    let list = [...teamMembers];
    const s = search.toLowerCase().trim();
    if (s) {
      list = list.filter(
        (member) =>
          (member.name || "").toLowerCase().includes(s) ||
          (member.email || "").toLowerCase().includes(s) ||
          (member.role || "").toLowerCase().includes(s),
      );
    }
    if (roleFilter) {
      list = list.filter((member) => member.role === roleFilter);
    }
    if (statusFilter === "active") {
      list = list.filter((member) => member.is_active);
    } else if (statusFilter === "inactive") {
      list = list.filter((member) => !member.is_active);
    }
    return list;
  }, [teamMembers, search, roleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const paginatedMembers = filteredMembers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-secondary">
            Team Management
          </h1>
          <p className="text-sm text-stone-600 mt-1">
            Manage your team members and their access roles
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Use each member&apos;s <span className="font-semibold">Progress</span> button in the table to open daily/weekly/monthly performance.
          </p>
        </div>
        <button
          onClick={() => {
            setShowAddModal(true);
            setFormData({
              name: "",
              email: "",
              role: "sales",
              password: "",
              report_permissions: { enabled: false, allowed_reports: [] },
            });
            setError("");
            setEmailError("");
          }}
          className="bg-primary hover:bg-primary/90 text-white font-medium rounded-lg px-4 py-2 shadow-sm hover:shadow-md transition-all flex items-center gap-2"
        >
          <UserPlus size={20} />
          Add Team Member
        </button>
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Total Members</p>
              <p className="text-2xl font-semibold text-secondary">
                {teamMembers.length}
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg">
              <Users size={24} className="text-primary" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Sales Team</p>
              <p className="text-2xl font-semibold text-secondary">
                {teamMembers.filter((m) => m.role === "sales").length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Finance Team</p>
              <p className="text-2xl font-semibold text-secondary">
                {teamMembers.filter((m) => m.role === "finance").length}
              </p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg">
              <Users size={24} className="text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-stone-600 mb-1">Active Members</p>
              <p className="text-2xl font-semibold text-secondary">
                {teamMembers.filter((m) => m.is_active).length}
              </p>
            </div>
            <div className="p-3 bg-teal-100 rounded-lg">
              <Users size={24} className="text-teal-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-stone-200">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder="Search by name, email, or role..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="">All roles</option>
              <option value="admin">Admin</option>
              <option value="sales">Sales</option>
              <option value="finance">Finance</option>
              <option value="management">Management</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <p className="form-helper mt-2">
            Showing {filteredMembers.length} of {teamMembers.length} team member(s)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-stone-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {paginatedMembers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-stone-500"
                  >
                    {teamMembers.length === 0
                      ? "No team members yet. Add your first team member to get started."
                      : "No team members match your filters."}
                  </td>
                </tr>
              ) : (
                paginatedMembers.map((member) => (
                  <tr
                    key={member.id}
                    className="hover:bg-stone-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium text-secondary">
                          {member.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {member.email}
                    </td>
                    <td className="px-6 py-4">{getRoleBadge(member.role)}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          member.is_active
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {member.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-stone-600">
                      {new Date(member.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(member)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => openPasswordModal(member)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Reset Password"
                        >
                          <Key size={18} />
                        </button>
                        <Link
                          to={`/team/${member.id}`}
                          className="px-2 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors whitespace-nowrap"
                          title="View Progress"
                        >
                          View
                        </Link>
                        {member.id !== currentUser?.id &&
                          member.role !== "admin" && (
                            <button
                              onClick={() => handleDeleteMember(member.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 bg-stone-50/50">
            <p className="text-sm text-stone-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-6 max-h-[88vh] overflow-hidden">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-xl font-semibold text-secondary">
                Add Team Member
              </h2>
            </div>
            <form onSubmit={handleAddMember} className="p-6 space-y-4 overflow-y-auto max-h-[calc(88vh-88px)]">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => formData.email && validateEmail(formData.email)}
                  required
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none ${
                    emailError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-stone-300"
                  }`}
                />
                {checkingEmail && (
                  <p className="mt-1 text-xs text-stone-500">
                    Checking email...
                  </p>
                )}
                {emailError && (
                  <p className="mt-1 text-xs text-red-600">{emailError}</p>
                )}
                {!emailError && formData.email && !checkingEmail && (
                  <p className="mt-1 text-xs text-emerald-600">
                    Email is available
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                >
                  <option value="sales">Sales</option>
                  <option value="finance">Finance</option>
                  <option value="management">Management</option>
                </select>
                <div className="mt-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <p className="text-xs font-semibold text-stone-700 mb-2">
                    {formData.role === "sales" && "Sales Role Permissions:"}
                    {formData.role === "finance" && "Finance Role Permissions:"}
                    {formData.role === "management" &&
                      "Management Role Permissions:"}
                  </p>
                  <ul className="text-xs text-stone-600 space-y-1">
                    {formData.role === "sales" && (
                      <>
                        <li>• Create new invoices</li>
                        <li>• View own invoices only</li>
                        <li>• Manage customers (add, edit, delete)</li>
                        <li>• View collection status for own invoices</li>
                        <li>• Record payments for own invoices</li>
                        <li>• View dashboard statistics</li>
                      </>
                    )}
                    {formData.role === "finance" && (
                      <>
                        <li>• Approve or reject invoices</li>
                        <li>• Edit invoice details (VAT, discount, amounts)</li>
                        <li>• View all company invoices</li>
                        <li>• Record payments for any approved invoice</li>
                        <li>• Manage income and expense entries</li>
                        <li>• Generate and export financial reports</li>
                        <li>• Remove VAT from invoices (set to 0%)</li>
                        <li>• Add discounts to invoices</li>
                      </>
                    )}
                    {formData.role === "management" && (
                      <>
                        <li>• View all company invoices</li>
                        <li>• View all company customers</li>
                        <li>• View all payments</li>
                        <li>• View income and expense records</li>
                        <li>• Generate and export all reports</li>
                        <li>• View dashboard statistics</li>
                        <li>• View collection status</li>
                      </>
                    )}
                  </ul>
                  <p className="text-[11px] text-stone-500 mt-2">
                    Report access is controlled per user below.
                  </p>
                </div>
              </div>
              <div className="border border-stone-200 rounded-lg p-3 bg-white">
                <p className="text-sm font-medium text-stone-700 mb-2">Reports Permission (per user)</p>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.report_permissions?.enabled)}
                    onChange={(e) => handleReportPermissionToggle(e.target.checked)}
                    className="w-4 h-4 text-primary border-stone-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm text-stone-700">
                    Enable Reports menu for this user
                  </span>
                </label>
                {formData.report_permissions?.enabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                    {REPORT_PERMISSION_OPTIONS.map((opt) => (
                      <label key={opt.key} className="flex items-center gap-2 text-xs text-stone-700">
                        <input
                          type="checkbox"
                          checked={Boolean(formData.report_permissions?.allowed_reports?.includes(opt.key))}
                          onChange={(e) => handleReportSelection(opt.key, e.target.checked)}
                          className="w-3.5 h-3.5 text-primary border-stone-300 rounded focus:ring-primary"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setError("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    submitting ||
                    checkingEmail ||
                    emailError === "This email is already registered"
                  }
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Adding..." : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-6 max-h-[88vh] overflow-hidden">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-xl font-semibold text-secondary">
                Edit Team Member
              </h2>
            </div>
            <form onSubmit={handleUpdateMember} className="p-6 space-y-4 overflow-y-auto max-h-[calc(88vh-88px)]">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={selectedUser.email}
                  disabled
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg bg-stone-50 text-stone-500"
                />
                <p className="text-xs text-stone-500 mt-1">
                  Email cannot be changed
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  disabled={selectedUser.role === "admin"}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none disabled:bg-stone-50 disabled:text-stone-500"
                >
                  <option value="sales">Sales</option>
                  <option value="finance">Finance</option>
                  <option value="management">Management</option>
                </select>
                {selectedUser.role === "admin" && (
                  <p className="text-xs text-stone-500 mt-1">
                    Admin role cannot be changed (subscription owner)
                  </p>
                )}
                <div className="mt-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
                  <p className="text-xs font-semibold text-stone-700 mb-2">
                    {formData.role === "sales" && "Sales Role Permissions:"}
                    {formData.role === "finance" && "Finance Role Permissions:"}
                    {formData.role === "management" &&
                      "Management Role Permissions:"}
                    {formData.role === "admin" && "Admin Role Permissions:"}
                  </p>
                  <ul className="text-xs text-stone-600 space-y-1">
                    {formData.role === "sales" && (
                      <>
                        <li>• Create new invoices</li>
                        <li>• View own invoices only</li>
                        <li>• Manage customers (add, edit, delete)</li>
                        <li>• View collection status for own invoices</li>
                        <li>• Record payments for own invoices</li>
                        <li>• View dashboard statistics</li>
                      </>
                    )}
                    {formData.role === "finance" && (
                      <>
                        <li>• Approve or reject invoices</li>
                        <li>• Edit invoice details (VAT, discount, amounts)</li>
                        <li>• View all company invoices</li>
                        <li>• Record payments for any approved invoice</li>
                        <li>• Manage income and expense entries</li>
                        <li>• Generate and export financial reports</li>
                        <li>• Remove VAT from invoices (set to 0%)</li>
                        <li>• Add discounts to invoices</li>
                      </>
                    )}
                    {formData.role === "management" && (
                      <>
                        <li>• View all company invoices</li>
                        <li>• View all company customers</li>
                        <li>• View all payments</li>
                        <li>• View income and expense records</li>
                        <li>• Generate and export all reports</li>
                        <li>• View dashboard statistics</li>
                        <li>• View collection status</li>
                      </>
                    )}
                    {formData.role === "admin" && (
                      <>
                        <li>• Full system access and team management</li>
                        <li>• Manage team members (add, edit, delete)</li>
                        <li>• Configure company settings</li>
                        <li>• All invoice, customer, and payment management</li>
                        <li>• All reports and analytics</li>
                      </>
                    )}
                  </ul>
                  <p className="text-[11px] text-stone-500 mt-2">
                    Report access is controlled per user below.
                  </p>
                </div>
              </div>
              <div className="border border-stone-200 rounded-lg p-3 bg-white">
                <p className="text-sm font-medium text-stone-700 mb-2">Reports Permission (per user)</p>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.report_permissions?.enabled)}
                    onChange={(e) => handleReportPermissionToggle(e.target.checked)}
                    className="w-4 h-4 text-primary border-stone-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm text-stone-700">
                    Enable Reports menu for this user
                  </span>
                </label>
                {formData.report_permissions?.enabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                    {REPORT_PERMISSION_OPTIONS.map((opt) => (
                      <label key={opt.key} className="flex items-center gap-2 text-xs text-stone-700">
                        <input
                          type="checkbox"
                          checked={Boolean(formData.report_permissions?.allowed_reports?.includes(opt.key))}
                          onChange={(e) => handleReportSelection(opt.key, e.target.checked)}
                          className="w-3.5 h-3.5 text-primary border-stone-300 rounded focus:ring-primary"
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="w-4 h-4 text-primary border-stone-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm font-medium text-stone-700">
                    Active
                  </span>
                </label>
              </div>
              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    setError("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {submitting ? "Updating..." : "Update Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-stone-200">
              <h2 className="text-xl font-semibold text-secondary">
                Reset Password
              </h2>
              <p className="text-sm text-stone-600 mt-1">
                Reset password for {selectedUser.name}
              </p>
            </div>
            <form onSubmit={handleResetPassword} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  New Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedUser(null);
                    setError("");
                  }}
                  className="px-6 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {submitting ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
